import React from 'react';
import { Link } from 'react-router-dom';
import { RiArrowLeftLine, RiEditLine, RiLoader4Line, RiRefreshLine, RiLockLine, RiSearchLine } from 'react-icons/ri';
import Layout from '../ui/Layout';
import Breadcrumb, { BreadcrumbItem } from '../ui/Breadcrumb';
import { usePermission } from '../../hooks/usePermission';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { cn } from '../../utils/cn';

/**
 * โครงหน้ารายละเอียด/แก้ไขแบบหน้าเต็ม — หน้าตาเดียวกับ MaintenanceViewPage / MaintenanceFormPage
 * (มติ PO 28 ต.ค. 2026: ดู/แก้ไขข้อมูลหลักเป็นหน้าเต็มมี URL ของตัวเอง แทนโมดัล)
 *
 *   Breadcrumb
 *   [ไอคอน] หัวข้อ #id                      [กลับ] [รีเฟรช] [แก้ไข] [actions…]
 *           คำอธิบาย
 *   ┌──────────── main (2/3) ───────────┐ ┌── sidebar (1/3) ──┐
 *   │ <DetailSection> …                 │ │ RecordMetaCard     │
 *   └───────────────────────────────────┘ │ EntityActivity…    │
 *                                         └────────────────────┘
 * มือถือ = คอลัมน์เดียว (sidebar ต่อท้าย) · ทุกคอลัมน์ min-w-0 กันตาราง/ข้อความยาวดันจอล้นแนวนอนที่ 375px
 *
 * สถานะ: `loading` → สปินเนอร์ · `error` → การ์ดตามชนิด (404 ไม่พบ / 403 ไม่มีสิทธิ์ / อื่น ๆ + ลองใหม่)
 * ส่ง error ดิบจาก axios/react-query มาได้เลย (อ่าน status + ข้อความไทยจาก envelope เอง)
 */
export interface DetailPageLayoutProps {
  breadcrumbs: BreadcrumbItem[];
  icon: React.ReactNode;
  title: React.ReactNode;
  /** แสดงเป็น "#id" สีจางต่อท้ายหัวข้อ */
  recordId?: number | string | null;
  subtitle?: React.ReactNode;
  /** ปลายทางปุ่ม "กลับ" (มักเป็น backTo จาก useDetailNavigation — รายการพร้อมตัวกรองเดิม) */
  backTo: string;
  backLabel?: string;
  onRefresh?: () => void;
  /** ปุ่ม "แก้ไข" — แสดงเมื่อมี editTo และผู้ใช้มี editPermission (ไม่ส่ง permission = แสดงเสมอ) */
  editTo?: string;
  editPermission?: string;
  /** state ที่ส่งไปกับการกดแก้ไข (ส่ง navState ของ useDetailNavigation ให้หน้าแก้ไขกลับรายการเดิมได้) */
  editState?: unknown;
  /** ปุ่มเพิ่มเติมท้ายแถว (ลบ / action อื่น — ต้อง gate สิทธิ์เอง) */
  actions?: React.ReactNode;
  loading?: boolean;
  error?: unknown;
  /** ข้อความเมื่อ 404 (เช่น "ไม่พบบริษัทที่ระบุ") */
  notFoundMessage?: string;
  onRetry?: () => void;
  /** คอลัมน์ขวา (ข้อมูลเพิ่มเติม + ประวัติกิจกรรม) · ไม่ส่ง = main เต็มความกว้าง */
  sidebar?: React.ReactNode;
  children?: React.ReactNode;
  /** element เพิ่ม (โมดัล action ของหน้า / ConfirmModal ของ useUnsavedChanges) — render นอก grid */
  overlays?: React.ReactNode;
}

export const headerButtonClass =
  'inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 text-sm font-medium rounded-lg border transition-colors';
export const headerButtonVariants = {
  neutral: 'text-secondary-700 bg-white border-secondary-300 hover:bg-secondary-50',
  primary: 'text-primary-700 bg-primary-50 border-primary-300 hover:bg-primary-100',
  danger: 'text-danger-700 bg-danger-50 border-danger-300 hover:bg-danger-100',
} as const;

const errorStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status ??
  (error as { status?: number } | null)?.status;

export const DetailPageLayout: React.FC<DetailPageLayoutProps> = ({
  breadcrumbs, icon, title, recordId, subtitle, backTo, backLabel = 'กลับ', onRefresh,
  editTo, editPermission, editState, actions, loading, error, notFoundMessage = 'ไม่พบข้อมูลที่ระบุ',
  onRetry, sidebar, children, overlays,
}) => {
  const { hasPermission } = usePermission();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center w-full min-h-[50vh]" data-testid="detail-loading">
          <div className="text-center">
            <RiLoader4Line className="h-12 w-12 text-primary-500 animate-spin mx-auto" />
            <p className="mt-4 text-secondary-500">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    const status = errorStatus(error);
    const [Icon, heading, message, tone] =
      status === 404
        ? [RiSearchLine, 'ไม่พบข้อมูล', notFoundMessage, 'secondary']
        : status === 403
        ? [RiLockLine, 'ไม่มีสิทธิ์เข้าถึง', getApiErrorMessage(error, 'คุณไม่มีสิทธิ์ดูข้อมูลนี้'), 'warning']
        : [RiRefreshLine, 'เกิดข้อผิดพลาด', getApiErrorMessage(error, 'ไม่สามารถโหลดข้อมูลได้'), 'danger'];
    return (
      <Layout>
        <div className="w-full">
          <Breadcrumb items={breadcrumbs} />
          <div
            className={cn(
              'rounded-lg border p-6',
              tone === 'danger' && 'bg-danger-50 border-danger-200',
              tone === 'warning' && 'bg-warning-50 border-warning-200',
              tone === 'secondary' && 'bg-white border-secondary-200'
            )}
            data-testid={`detail-error-${status ?? 'other'}`}
          >
            <h3 className="text-lg font-medium text-secondary-900 mb-2 flex items-center gap-2">
              <Icon className="h-5 w-5" />
              {heading}
            </h3>
            <p className="text-secondary-700 [overflow-wrap:anywhere]">{message}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to={backTo} className={cn(headerButtonClass, headerButtonVariants.neutral)}>
                <RiArrowLeftLine className="h-4 w-4 mr-1.5" />
                กลับไปหน้ารายการ
              </Link>
              {status !== 404 && status !== 403 && onRetry && (
                <button type="button" onClick={onRetry} className={cn(headerButtonClass, headerButtonVariants.primary)}>
                  <RiRefreshLine className="h-4 w-4 mr-1.5" />
                  ลองอีกครั้ง
                </button>
              )}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const showEdit = !!editTo && (!editPermission || hasPermission(editPermission));

  return (
    <Layout>
      {overlays}
      <div className="w-full min-w-0">
        <Breadcrumb items={breadcrumbs} />

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm text-white [&>svg]:w-5 [&>svg]:h-5">
              {icon}
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-secondary-900 leading-tight [overflow-wrap:anywhere]">
                {title}
                {recordId !== undefined && recordId !== null && recordId !== '' && (
                  <span className="text-secondary-500 font-normal"> #{recordId}</span>
                )}
              </h1>
              {subtitle && <p className="text-xs text-secondary-500 mt-0.5 [overflow-wrap:anywhere]">{subtitle}</p>}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* <Link> ไม่ใช่ปุ่มที่สั่ง navigate เอง: useUnsavedChanges ดักคลิก <a> ภายในแอพ → หน้าแก้ไขที่ยังไม่บันทึกได้กล่องยืนยัน */}
            <Link to={backTo} className={cn(headerButtonClass, headerButtonVariants.neutral)}>
              <RiArrowLeftLine className="h-4 w-4 mr-1.5" />
              {backLabel}
            </Link>
            {onRefresh && (
              <button type="button" onClick={onRefresh} className={cn(headerButtonClass, headerButtonVariants.neutral)}>
                <RiRefreshLine className="h-4 w-4 mr-1.5" />
                รีเฟรช
              </button>
            )}
            {showEdit && (
              <Link to={editTo!} state={editState} className={cn(headerButtonClass, headerButtonVariants.primary)}>
                <RiEditLine className="h-4 w-4 mr-1.5" />
                แก้ไข
              </Link>
            )}
            {actions}
          </div>
        </div>

        {sidebar ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6 min-w-0">{children}</div>
            <div className="space-y-6 min-w-0">{sidebar}</div>
          </div>
        ) : (
          <div className="space-y-6 min-w-0">{children}</div>
        )}
      </div>
    </Layout>
  );
};

export default DetailPageLayout;
